import { FormEvent, useEffect, useMemo, useState } from 'react';
import { admin, Conversion, InputProfile, LiveValue, LiveValues, Mapping, SensorRow, trackers, ValueGroup, VehicleDetail } from '../api';
import { ago, dateTime, dayLabel, time } from '../format';
import { Flag } from './Flag';
import { SearchSelect } from './SearchSelect';

// Input mapping and the tracker's live values in one box: each value the tracker sends can be
// mapped to a meaning (ignition, fuel used…) straight from its row, with a preview of the
// converted value. Input profiles apply several mappings in one go.

export const MEANINGS: Record<string, { label: string; unit: string }> = {
  ignition: { label: 'Ignition', unit: 'on/off' },
  power: { label: 'Supply voltage', unit: 'V' },
  odometer: { label: 'Odometer', unit: 'km' },
  tacho_distance: { label: 'Tachograph total distance', unit: 'km' },
  fuel_level: { label: 'Fuel level', unit: '%' },
  fuel_used: { label: 'Total fuel used (counter, gives fuel per journey)', unit: 'L' },
  door: { label: 'Door', unit: 'open/closed' },
  fridge: { label: 'Fridge on/off (shows a snowflake on the live list)', unit: 'on/off' },
};

export const meaningLabel = (m: string) => MEANINGS[m]?.label
  ?? (m.startsWith('temperature:') ? `Temperature: ${m.slice(12)}` : m.startsWith('setpoint:') ? `Setpoint: ${m.slice(9)}` : m.replace(/^custom:/, ''));
export const isTemperature = (m: string) => m.startsWith('temperature:');
// Temperatures and fridge setpoints: named inputs in °C, same conversion and fault rule.
const NAMED: Record<string, string> = { temperature: 'temperature:', setpoint: 'setpoint:' };
const namedKind = (m: string) => Object.keys(NAMED).find((k) => m.startsWith(NAMED[k])) ?? null;
const shortLabel = (m: string) => meaningLabel(m).replace(/ \(.*\)$/, '');
export const fromLabel = (iso: string | null) => (iso ? `from ${dayLabel(iso)} ${time(iso)}` : 'from the start');
export const toIso = (local: string | null) => (local === null ? null : new Date(local).toISOString());
// An ISO time as a datetime-local value on this device.
const toLocal = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

export function conversionText(c: Conversion): string {
  const parts: string[] = [];
  if (c.signed) parts.push(`signed ${c.signed}-bit`);
  if (c.bit !== undefined) parts.push(`bit ${c.bit}`);
  if (c.multiply !== undefined) parts.push(`× ${c.multiply}`);
  if (c.offset !== undefined) parts.push(`${c.offset < 0 ? '−' : '+'} ${Math.abs(c.offset)}`);
  if (c.threshold !== undefined) parts.push(`on if above ${c.threshold}`);
  if (c.invert) parts.push('inverted');
  if (c.invalid?.length) parts.push(`no reading: ${c.invalid.join(', ')}`);
  return parts.join(', ') || 'as sent';
}

// Same conversion as the journey calculation (ingest/src/journeys/sensors.ts).
function convert(raw: unknown, c: Conversion): number | boolean | null {
  let v: number | boolean;
  if (typeof raw === 'boolean') v = raw;
  else if (typeof raw === 'number' && Number.isFinite(raw)) v = raw;
  else if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) v = Number(raw);
  else return null;
  if (c.invalid?.length && typeof v === 'number' && c.invalid.some((x) => Math.abs(x - (v as number)) < 1e-9)) return null;
  if (c.signed && typeof v === 'number') {
    const span = 2 ** c.signed;
    v = Math.trunc(v) % span;
    if (v >= span / 2) v -= span;
  }
  if (c.bit !== undefined) v = (Math.trunc(Number(v)) >> c.bit) & 1;
  if (c.multiply !== undefined) v = Number(v) * c.multiply;
  if (c.offset !== undefined) v = Number(v) + c.offset;
  if (c.threshold !== undefined) v = Number(v) > c.threshold;
  if (c.invert) v = typeof v === 'boolean' ? !v : v === 0;
  return v;
}

const asSent = (v: unknown) => (typeof v === 'boolean' ? (v ? 'on' : 'off') : String(v));
const num = (v: number) => Number(v.toPrecision(10)).toLocaleString('en-IE', { maximumFractionDigits: 3, useGrouping: false });

function converted(raw: unknown, m: Pick<Mapping, 'conversion' | 'unit'> & { meaning?: string }): string {
  let v = convert(raw, m.conversion);
  // Temperatures outside −60…+150 are sensor faults (same rule as the server).
  if (m.meaning && namedKind(m.meaning) && typeof v === 'number' && (v < -60 || v > 150)) v = null;
  if (v === null) return 'no reading';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return m.unit && !m.unit.includes('/') ? `${num(v)} ${m.unit}` : num(v);
}

// "All history" or a date and time typed on this device.
export function ApplyFrom({ id, value, onChange }: { id: string; value: string | null; onChange: (v: string | null) => void }) {
  return (
    <fieldset className="apply">
      <legend>Apply</legend>
      <label className="check"><input type="radio" name={id} checked={value === null} onChange={() => onChange(null)} /> To all history (recalculates everything)</label>
      <label className="check"><input type="radio" name={id} checked={value !== null} onChange={() => onChange('')} /> From a date and time:</label>
      {value !== null && <input id={`${id}-when`} type="datetime-local" required value={value} onChange={(e) => onChange(e.target.value)} />}
    </fieldset>
  );
}

const GROUPS: Array<[ValueGroup, string]> = [
  ['gps', 'GPS'],
  ['gsm', 'GSM'],
  ['power', 'Power'],
  ['ignition', 'Ignition, motion and inputs'],
  ['can', 'CAN and vehicle data'],
  ['temperature', 'Temperature'],
  ['other', 'Other / not named'],
];
const WINDOWS = [['1h', 'Last hour'], ['24h', 'Last 24 h'], ['all', 'All (90 days)']] as const;
type Window = (typeof WINDOWS)[number][0];

function show(v: LiveValue): string {
  let text: string;
  if (typeof v.value === 'boolean') text = v.key === 'gps:valid' ? (v.value ? 'yes' : 'no') : v.value ? 'on' : 'off';
  // No thousands separators: many values are codes (cell ID, operator).
  else if (typeof v.value === 'number') text = v.value.toLocaleString('en-IE', { maximumFractionDigits: 3, useGrouping: false });
  else if (v.value === null || v.value === undefined) text = '–';
  else text = String(v.value);
  if (v.label) text += ` (${v.label})`;
  if (v.unit && typeof v.value === 'number') text += ` ${v.unit}`;
  return text;
}

// A first guess at what a live value means, from its Teltonika name. DIN1 is ignition on
// most of our installs, so it is suggested while the vehicle has no ignition input.
function guessMeaning(r: LiveValue, mapped: Map<string, SensorRow>): string {
  const n = r.name.toLowerCase();
  if (n.includes('set temperature') || n.includes('setpoint') || n.includes('set point')) return 'setpoint';
  if (r.temp || r.group === 'temperature' || r.unit === '°C') return 'temperature';
  if (n.includes('ignition') || (r.io === '1' && !mapped.has('ignition'))) return 'ignition';
  if (r.io === '66' || n.includes('external voltage')) return 'power';
  if (n.includes('tacho') && (n.includes('distance') || n.includes('mileage') || n.includes('odometer'))) return 'tacho_distance';
  if (n.includes('odometer') || n.includes('mileage')) return 'odometer';
  if (n.includes('fuel level')) return 'fuel_level';
  if (n.includes('fuel') && (n.includes('used') || n.includes('consumed'))) return 'fuel_used';
  if (n.includes('door')) return 'door';
  return 'custom';
}
const customName = (name: string) => name.toLowerCase().replace(/[^a-z0-9_ -]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);

// --- The mapping form --------------------------------------------------------------------

interface FormState {
  meaning: string; custom: string; sourceIo: string; unit: string;
  multiply: string; offset: string; threshold: string; bit: string; invert: boolean;
  // Temperature inputs: name (in `custom`), allowed range, signed width, fault codes.
  min: string; max: string; signed: string; invalid: string;
}
const s = (v: number | undefined) => (v === undefined ? '' : String(v));

function formFromSensor(m: Mapping): FormState {
  const c = m.conversion;
  const named = namedKind(m.meaning);
  const custom = !MEANINGS[m.meaning];
  return {
    meaning: named ?? (custom ? 'custom' : m.meaning),
    custom: named ? m.meaning.slice(NAMED[named].length) : custom ? m.meaning.replace(/^custom:/, '') : '', sourceIo: m.sourceIo,
    unit: m.unit ?? '', multiply: s(c.multiply), offset: s(c.offset), threshold: s(c.threshold), bit: s(c.bit), invert: !!c.invert,
    min: s(m.limits?.min), max: s(m.limits?.max), signed: s(c.signed), invalid: (c.invalid ?? []).join(', '),
  };
}

// "Dallas Temperature 1" → "dallas 1"; staff usually rename it (e.g. "front compartment").
const tempName = (name: string) => customName(name.replace(/temperature/i, '').replace(/#/g, '')) || 'sensor';
// "Zone 1 Set Temperature" → "zone 1".
const setpointName = (name: string) => customName(name.replace(/set ?(temperature|point)/i, '').replace(/#/g, '')) || 'zone 1';

function formFromValue(r: LiveValue, mapped: Map<string, SensorRow>): FormState {
  const meaning = guessMeaning(r, mapped);
  const temp = meaning === 'temperature' || meaning === 'setpoint';
  return {
    meaning, custom: meaning === 'setpoint' ? setpointName(r.name) : temp ? tempName(r.name) : meaning === 'custom' ? customName(r.name) || `io ${r.io}` : '', sourceIo: r.io!,
    unit: temp ? '°C' : MEANINGS[meaning]?.unit ?? r.unit ?? '', multiply: s(r.scale ?? undefined), offset: '', threshold: '', bit: '', invert: false,
    min: '', max: '', signed: s(r.temp?.signed), invalid: (r.temp?.invalid ?? []).join(', '),
  };
}

function mappingOf(f: FormState): Mapping {
  const conversion: Conversion = {};
  if (f.bit !== '') conversion.bit = Number(f.bit);
  if (f.multiply !== '') conversion.multiply = Number(f.multiply);
  if (f.offset !== '') conversion.offset = Number(f.offset);
  if (f.threshold !== '') conversion.threshold = Number(f.threshold);
  if (f.invert) conversion.invert = true;
  if (f.signed) conversion.signed = Number(f.signed) as 8 | 16 | 32;
  const codes = f.invalid.split(/[,\s]+/).filter(Boolean).map(Number).filter(Number.isFinite);
  if (codes.length) conversion.invalid = codes;
  const name = f.custom.trim().toLowerCase();
  const meaning = f.meaning === 'custom' ? `custom:${name}` : NAMED[f.meaning] ? `${NAMED[f.meaning]}${name}` : f.meaning;
  const limits = f.meaning === 'temperature' && (f.min !== '' || f.max !== '')
    ? { ...(f.min !== '' ? { min: Number(f.min) } : {}), ...(f.max !== '' ? { max: Number(f.max) } : {}) } : null;
  return { meaning, sourceIo: f.sourceIo, conversion, unit: f.unit || null, limits };
}

interface Editing { anchor: string; form: FormState; from: string | null }

function MapForm({ vehicleId, editing, current, rawOf, onCancel, onSaved }: {
  vehicleId: number; editing: Editing; current: Map<string, SensorRow>; rawOf: (io: string) => LiveValue | undefined;
  onCancel: () => void; onSaved: (msg: string) => void;
}) {
  const [f, setF] = useState(editing.form);
  const [from, setFrom] = useState(editing.from);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const m = mappingOf(f);
  const src = rawOf(f.sourceIo);
  const replaces = current.get(m.meaning);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await admin.saveSensor(vehicleId, { ...m, validFrom: toIso(from) });
      onSaved(`Input "${meaningLabel(m.meaning)}" saved; journeys recalculated.`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="form map-form">
      <div className="grid3">
        <label>Meaning
          <select value={f.meaning} onChange={(e) => setF({ ...f, meaning: e.target.value, unit: NAMED[e.target.value] ? '°C' : MEANINGS[e.target.value]?.unit ?? f.unit })}>
            {Object.entries(MEANINGS).map(([k, x]) => <option key={k} value={k}>{x.label}</option>)}
            <option value="temperature">Temperature (name it)…</option>
            <option value="setpoint">Fridge setpoint (name it)…</option>
            <option value="custom">Other (name it)…</option>
          </select>
        </label>
        {(f.meaning === 'custom' || NAMED[f.meaning]) && (
          <label>Name<input type="text" required pattern="[A-Za-z0-9_ \-]{1,40}" value={f.custom} onChange={(e) => setF({ ...f, custom: e.target.value })}
            placeholder={f.meaning === 'temperature' ? 'e.g. front compartment' : f.meaning === 'setpoint' ? 'e.g. zone 1' : undefined} /></label>
        )}
        {NAMED[f.meaning] && (
          <>
            {f.meaning === 'temperature' && <>
              <label>Allowed from<input type="number" step="any" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} placeholder="e.g. 2" /></label>
              <label>Allowed to<input type="number" step="any" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} placeholder="e.g. 8" /></label>
            </>}
            <label>Signed number
              <select value={f.signed} onChange={(e) => setF({ ...f, signed: e.target.value })}>
                <option value="">No</option><option value="16">16-bit</option><option value="32">32-bit</option>
              </select>
            </label>
            <label>No-reading codes<input type="text" inputMode="decimal" value={f.invalid} onChange={(e) => setF({ ...f, invalid: e.target.value })} placeholder="e.g. 850, 3000" /></label>
          </>
        )}
        <label>IO number<input type="text" inputMode="numeric" required pattern="[0-9]{1,5}" value={f.sourceIo} onChange={(e) => setF({ ...f, sourceIo: e.target.value })} placeholder="e.g. 1" /></label>
        <label>Unit<input type="text" maxLength={20} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></label>
        <label>Multiply by<input type="number" step="any" value={f.multiply} onChange={(e) => setF({ ...f, multiply: e.target.value })} placeholder="e.g. 0.001" /></label>
        <label>Then add<input type="number" step="any" value={f.offset} onChange={(e) => setF({ ...f, offset: e.target.value })} /></label>
        <label>On if above<input type="number" step="any" value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} /></label>
        <label>Use bit<input type="number" min={0} max={63} step={1} value={f.bit} onChange={(e) => setF({ ...f, bit: e.target.value })} /></label>
        <label className="check"><input type="checkbox" checked={f.invert} onChange={(e) => setF({ ...f, invert: e.target.checked })} /> Invert (on ↔ off)</label>
      </div>
      <p className="preview" aria-live="polite">
        {src
          ? <>Now: <span className="mono">{asSent(src.raw ?? src.value)}</span> as sent → <b className="mono">{converted(src.raw ?? src.value, m)}</b></>
          : <span className="muted">IO {f.sourceIo || '?'} hasn't been received in this window, so there is no preview.</span>}
      </p>
      <p className="muted small">
        Leave conversion boxes empty to use the value as sent. Applied in order: no-reading codes, signed, bit, multiply, add, on-if-above, invert.
        {f.meaning === 'temperature' && <> Temperatures outside −60 to +150 °C count as a sensor fault. Readings outside the allowed range show in red, for staff and the customer.</>}
        {f.meaning === 'setpoint' && <> The temperature the fridge is set to, shown with the temperatures for staff and the customer. Name it after the zone it belongs to.</>}
        {f.meaning === 'fridge' && <> On means the fridge is running. Any vehicle with this input gets a snowflake on the live map list (crossed out when off), for staff and the customer.</>}
        {replaces && replaces.sourceIo !== f.sourceIo && <> Replaces the current {shortLabel(m.meaning)} input (IO {replaces.sourceIo}).</>}
      </p>
      <ApplyFrom id={`from-${editing.anchor}`} value={from} onChange={setFrom} />
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>Save input</button>
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// --- Input profiles ----------------------------------------------------------------------

function ApplyProfile({ vehicleId, model, current, rawOf, onCancel, onSaved }: {
  vehicleId: number; model: string | null; current: Map<string, SensorRow>; rawOf: (io: string) => LiveValue | undefined;
  onCancel: () => void; onSaved: (msg: string) => void;
}) {
  const [profiles, setProfiles] = useState<InputProfile[] | null>(null);
  const [pick, setPick] = useState<number | null>(null);
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    admin.profiles().then(({ profiles: list }) => {
      // Profiles made for this tracker model first.
      const sorted = [...list].sort((a, b) => Number(b.model === model && !!model) - Number(a.model === model && !!model));
      setProfiles(sorted);
      setPick(sorted[0]?.id ?? null);
    }).catch((e) => setError(e.message));
  }, [model]);

  const profile = profiles?.find((p) => p.id === pick) ?? null;
  const items = profile?.items.filter((i) => !skip.has(i.meaning)) ?? [];

  const apply = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile || items.length === 0) return;
    setBusy(true);
    setError('');
    try {
      await admin.saveSensors(vehicleId, { validFrom: toIso(from), items, profile: profile.name });
      onSaved(`Profile "${profile.name}" applied (${items.length} input${items.length === 1 ? '' : 's'}); journeys recalculated.`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!profile || !window.confirm(`Delete the profile "${profile.name}"? Vehicles it was applied to keep their inputs.`)) return;
    try {
      await admin.deleteProfile(profile.id);
      const rest = profiles!.filter((p) => p.id !== profile.id);
      setProfiles(rest);
      setPick(rest[0]?.id ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form className="form subpanel" onSubmit={apply}>
      <h3>Apply an input profile</h3>
      {profiles === null && !error && <p className="muted">Loading…</p>}
      {profiles?.length === 0 && <p className="muted">No profiles yet. Map this vehicle's inputs, then use <b>Save as profile</b> to reuse them on other vehicles.</p>}
      {profiles && profiles.length > 0 && (
        <>
          <div className="actions wrap">
            <label className="sr-only" htmlFor="profile-pick">Profile</label>
            <SearchSelect id="profile-pick" value={pick ? String(pick) : ''} onChange={(v) => { setPick(Number(v)); setSkip(new Set()); }}
              placeholder="Choose a profile…" options={profiles.map((p) => ({ value: String(p.id), label: p.name, detail: p.model ?? undefined }))} />
            {profile && <button type="button" className="link danger" onClick={() => void remove()}>Delete profile</button>}
          </div>
          {profile?.notes && <p className="muted small">{profile.notes}</p>}
          {profile?.model && model && profile.model !== model && (
            <p className="warn small">Made for {profile.model}; this tracker is {model}. Check the IO numbers mean the same.</p>
          )}
          {profile && (
            <div className="table-wrap">
              <table>
                <thead><tr><th><span className="sr-only">Use</span></th><th>Meaning</th><th>IO</th><th>Conversion</th><th>Now</th></tr></thead>
                <tbody>
                  {profile.items.map((i) => {
                    const src = rawOf(i.sourceIo);
                    const was = current.get(i.meaning);
                    return (
                      <tr key={i.meaning} className={skip.has(i.meaning) ? 'off' : undefined}>
                        <td><input type="checkbox" aria-label={`Use ${shortLabel(i.meaning)}`} checked={!skip.has(i.meaning)}
                          onChange={(e) => { const n = new Set(skip); if (e.target.checked) n.delete(i.meaning); else n.add(i.meaning); setSkip(n); }} /></td>
                        <td>{shortLabel(i.meaning)}{was && was.sourceIo !== i.sourceIo && <span className="muted small"> · replaces IO {was.sourceIo}</span>}</td>
                        <td className="num">{i.sourceIo}</td>
                        <td>{conversionText(i.conversion)}</td>
                        <td className="num">{src ? converted(src.raw ?? src.value, i) : <span className="muted">not received</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <ApplyFrom id="profile-from" value={from} onChange={setFrom} />
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        {profiles && profiles.length > 0 && <button type="submit" className="primary" disabled={busy || items.length === 0}>Apply {items.length} input{items.length === 1 ? '' : 's'}</button>}
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function SaveProfile({ mappings, model, onCancel, onSaved }: {
  mappings: SensorRow[]; model: string | null; onCancel: () => void; onSaved: (msg: string) => void;
}) {
  const [profiles, setProfiles] = useState<InputProfile[]>([]);
  const [name, setName] = useState('');
  const [forModel, setForModel] = useState(model ?? '');
  const [notes, setNotes] = useState('');
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { admin.profiles().then((r) => setProfiles(r.profiles)).catch(() => {}); }, []);
  const existing = profiles.find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
  const items: Mapping[] = mappings.filter((m) => !skip.has(m.meaning))
    .map(({ meaning, sourceIo, conversion, unit, limits }) => ({ meaning, sourceIo, conversion, unit, limits: limits ?? null }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (existing && !window.confirm(`Replace the profile "${existing.name}"? Vehicles it was applied to keep their inputs.`)) return;
    setBusy(true);
    setError('');
    try {
      await admin.saveProfile({ name: name.trim(), model: forModel || null, notes: notes.trim() || null, items }, existing?.id);
      onSaved(`Profile "${name.trim()}" saved with ${items.length} input${items.length === 1 ? '' : 's'}.`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form className="form subpanel" onSubmit={submit}>
      <h3>Save these inputs as a profile</h3>
      <p className="muted small">A profile is a template: applying it copies its inputs to a vehicle. Changing it later doesn't change vehicles it was applied to.</p>
      <div className="grid3">
        <label>Profile name<input type="text" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FMC920 truck, ignition DIN1" list="profile-names" /></label>
        <datalist id="profile-names">{profiles.map((p) => <option key={p.id} value={p.name} />)}</datalist>
        <label>Made for model<input type="text" maxLength={40} value={forModel} onChange={(e) => setForModel(e.target.value)} placeholder="any" /></label>
      </div>
      <label>Notes<input type="text" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. fuel counter from FMS, needs CAN adapter" /></label>
      <fieldset className="apply">
        <legend>Inputs to include</legend>
        {mappings.map((m) => (
          <label key={m.meaning} className="check">
            <input type="checkbox" checked={!skip.has(m.meaning)}
              onChange={(e) => { const n = new Set(skip); if (e.target.checked) n.delete(m.meaning); else n.add(m.meaning); setSkip(n); }} />
            {' '}{shortLabel(m.meaning)} <span className="muted small">IO {m.sourceIo} · {conversionText(m.conversion)}</span>
          </label>
        ))}
      </fieldset>
      {existing && <p className="warn small">A profile called "{existing.name}" exists; saving replaces it.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button type="submit" className="primary" disabled={busy || items.length === 0}>{existing ? 'Replace profile' : 'Save profile'}</button>
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// --- The box -----------------------------------------------------------------------------

// `vehicle` gives the mapping (it belongs to the vehicle), `tracker` the live values. A unit
// may have only one of the two.
export function InputMapping({ vehicle, tracker, canEdit, onSaved }: {
  vehicle: VehicleDetail | null; tracker: { id: number; model: string | null } | null; canEdit: boolean; onSaved: (msg: string) => void;
}) {
  const [win, setWin] = useState<Window>('24h');
  const [data, setData] = useState<LiveValues | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [panel, setPanel] = useState<'apply' | 'save' | null>(null);
  const trackerId = tracker?.id ?? null;
  const model = tracker?.model ?? null;

  useEffect(() => {
    if (trackerId === null) return;
    setError('');
    trackers.values(trackerId, win).then(setData).catch((e) => setError(e.message));
  }, [trackerId, win, model]);

  const rows = data?.rows ?? null;
  const byIo = useMemo(() => new Map((rows ?? []).filter((r) => r.key.startsWith('io:')).map((r) => [r.io!, r])), [rows]);
  const rawOf = (io: string) => byIo.get(io);
  const sensors = vehicle?.sensors ?? [];
  // The mapping in force now, per meaning (sensors come newest first per meaning).
  const current = useMemo(() => {
    const now = new Date().toISOString();
    const m = new Map<string, SensorRow>();
    for (const x of sensors) if (!m.has(x.meaning) && (x.validFrom === null || x.validFrom <= now)) m.set(x.meaning, x);
    return m;
  }, [sensors]);
  const mappedTo = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const x of current.values()) m.set(x.sourceIo, [...(m.get(x.sourceIo) ?? []), shortLabel(x.meaning)]);
    return m;
  }, [current]);

  const mayMap = !!vehicle && canEdit;
  const done = (msg: string) => { setEditing(null); setPanel(null); onSaved(msg); };
  const open = (e: Editing) => { setPanel(null); setEditing(e); };

  const remove = async (x: SensorRow) => {
    if (!vehicle || !window.confirm(`Remove the "${meaningLabel(x.meaning)}" input (${fromLabel(x.validFrom)})?`)) return;
    try {
      await admin.deleteSensor(vehicle.vehicle.id, x.id);
      onSaved(`Input "${meaningLabel(x.meaning)}" removed.`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // A value already mapped opens its mapping; otherwise a new one with a guessed meaning.
  const mapValue = (r: LiveValue): Editing => {
    const existing = [...current.values()].find((x) => x.sourceIo === r.io);
    return existing
      ? { anchor: r.key, form: formFromSensor(existing), from: existing.validFrom && toLocal(existing.validFrom) }
      : { anchor: r.key, form: formFromValue(r, current), from: null };
  };

  const form = (anchor: string) => editing?.anchor === anchor && vehicle && (
    <MapForm key={anchor} vehicleId={vehicle.vehicle.id} editing={editing} current={current} rawOf={rawOf}
      onCancel={() => setEditing(null)} onSaved={done} />
  );

  return (
    <section className="card form inputs-box" aria-label="Inputs and live values">
      <div className="page-head">
        <h2>Inputs and live values</h2>
        {mayMap && (
          <div className="actions wrap">
            <button type="button" onClick={() => { setEditing(null); setPanel(panel === 'apply' ? null : 'apply'); }} aria-expanded={panel === 'apply'}>Apply profile</button>
            {current.size > 0 && <button type="button" onClick={() => { setEditing(null); setPanel(panel === 'save' ? null : 'save'); }} aria-expanded={panel === 'save'}>Save as profile</button>}
          </div>
        )}
      </div>

      {vehicle && (
        <>
          <p className="muted small">
            Which tracker input means what, and how to convert it. {mayMap && tracker && <>Pick <b>Map</b> on any live value below to map it.</>}
            {!canEdit && ' Only admins can change these.'}
          </p>
          {panel === 'apply' && <ApplyProfile vehicleId={vehicle.vehicle.id} model={model} current={current} rawOf={rawOf} onCancel={() => setPanel(null)} onSaved={done} />}
          {panel === 'save' && <SaveProfile mappings={[...current.values()]} model={model} onCancel={() => setPanel(null)} onSaved={done} />}
          {sensors.length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Meaning</th><th>IO</th><th>Conversion</th>{tracker && <th>Now</th>}<th>Applies</th><th><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {sensors.map((x) => {
                    const live = current.get(x.meaning) === x;
                    const src = rawOf(x.sourceIo);
                    return [
                      <tr key={x.id} className={live ? undefined : 'off'}>
                        <td>{shortLabel(x.meaning)}{!live && <span className="muted small"> · earlier</span>}</td>
                        <td className="num">{x.sourceIo}</td>
                        <td>{conversionText(x.conversion)}{x.unit && <span className="muted"> · {x.unit}</span>}
                          {x.limits && <div className="muted small">allowed {x.limits.min ?? '…'} to {x.limits.max ?? '…'}{x.unit ? ` ${x.unit}` : ''}</div>}</td>
                        {tracker && <td className="num">{live ? (src ? converted(src.raw ?? src.value, x) : <span className="muted">–</span>) : ''}</td>}
                        <td className="small">{fromLabel(x.validFrom)}</td>
                        <td className="row-actions">
                          {canEdit && <>
                            <button type="button" className="link" onClick={() => open({ anchor: `s${x.id}`, form: formFromSensor(x), from: x.validFrom && toLocal(x.validFrom) })}>Edit</button>
                            <button type="button" className="link danger" onClick={() => void remove(x)}>Remove</button>
                          </>}
                        </td>
                      </tr>,
                      editing?.anchor === `s${x.id}` && <tr key={`${x.id}-f`} className="form-row"><td colSpan={6}>{form(`s${x.id}`)}</td></tr>,
                    ];
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="muted">No inputs mapped yet.</p>}
          {mayMap && editing?.anchor !== 'new' && (
            <div className="actions"><button type="button" className="link" onClick={() => open({ anchor: 'new', from: null, form: formFromSensor({ meaning: 'ignition', sourceIo: '', conversion: {}, unit: 'on/off' }) })}>+ Add an input by IO number</button></div>
          )}
          {form('new')}
        </>
      )}

      {tracker && (
        <>
          <div className="page-head">
            <h3>Live values</h3>
            <div className="seg" role="group" aria-label="Time window">
              {WINDOWS.map(([w, label]) => (
                <button key={w} type="button" aria-pressed={win === w} onClick={() => setWin(w)}>{label}</button>
              ))}
            </div>
          </div>
          {data && (data.family
            ? <p className="muted small">IO names for {data.model} ({data.family}), from Teltonika's AVL ID list.</p>
            : <p className="muted small">Tracker model not set, so only IO IDs that mean the same on every Teltonika model are named. Set the model under Device &amp; SIM.</p>)}
          {!vehicle && <p className="muted small">Inputs are mapped per vehicle: install this tracker in a vehicle to map them.</p>}
          {error && <p className="error" role="alert">{error}</p>}
          {rows && rows.length === 0 && <p className="muted">Nothing received in this window.</p>}
          {rows && rows.length > 0 && (
            <div className="table-wrap live-scroll" tabIndex={0} aria-label="Live values">
              <table className="values">
                <tbody>
                  {GROUPS.map(([g, title]) => {
                    // Mapped values are in the table above, so they aren't repeated here.
                    const list = rows.filter((r) => r.group === g && !r.key.startsWith('map:'))
                      .sort((a, b) => (a.io === null ? -1 : b.io === null ? 1 : Number(a.io) - Number(b.io)) || a.name.localeCompare(b.name));
                    if (list.length === 0) return null;
                    return [
                      <tr key={g} className="group"><th colSpan={mayMap ? 4 : 3} scope="colgroup">{title}</th></tr>,
                      ...list.flatMap((r) => {
                        const as = r.io ? mappedTo.get(r.io) : undefined;
                        return [
                          <tr key={r.key}>
                            <td>{r.name}{r.io && r.name !== `IO ${r.io}` && <span className="muted small"> · IO {r.io}</span>}
                              {as && <span className="mapped" title="Mapped input"> → {as.join(', ')}</span>}</td>
                            <td className="num"><Flag country={r.country} name={r.countryName} />{show(r)}</td>
                            <td className="small muted">{r.updated && <time dateTime={r.updated} title={dateTime(r.updated)}>{ago(r.updated)}</time>}</td>
                            {mayMap && (
                              <td className="row-actions">
                                {r.io && <button type="button" className="link" aria-label={`Map ${r.name}`}
                                  onClick={() => open(mapValue(r))}>{as ? 'Edit' : 'Map'}</button>}
                              </td>
                            )}
                          </tr>,
                          editing?.anchor === r.key && <tr key={`${r.key}-f`} className="form-row"><td colSpan={4}>{form(r.key)}</td></tr>,
                        ];
                      }),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
